import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Copy } from 'lucide-react';
import styles from './VLAAdminShared.module.css';
import jobsTableStyles from './JobsTable.module.css';
import cellStyles from '../../Data/UploadDashboard/AssignedJobsSection.module.css';
import { PRISMAX_DATA_PIPELINE_URL } from '../../../configs/config';
import { SwapMainAppContext } from '../../../context/SwapMainAppContext';
import { getApiErrorMessage, isSessionExpiredError } from '../../../utils/authErrors';
import { fmtUSD, paymentState } from '../../Data/UploadDashboard/Jobs/jobsApi';
import { JobCell, TaskChips, ProgressCell, AmountCell, DueCell } from '../../Data/UploadDashboard/Jobs/JobRowCells';
import JobFormModal from '../../Data/UploadDashboard/Jobs/JobFormModal';
import JobDeleteModal from '../../Data/UploadDashboard/Jobs/JobDeleteModal';
import UnmarkPaymentsModal from '../../Data/UploadDashboard/Jobs/UnmarkPaymentsModal';
import JobDetailDrawer from '../../Data/UploadDashboard/Jobs/JobDetailDrawer';

const PAGE_SIZE = 15;
const SEARCH_PATTERN = /^(JOB|ORD)-(\d+)$/i;

const CHAIN_COLORS = {
    Solana:   { bg: '#1a2a1a', color: '#4caf7d' },
    Ethereum: { bg: '#1a1a2e', color: '#7b8fd4' },
    Base:     { bg: '#0f1e2e', color: '#4e8fd4' },
    Monad:    { bg: '#1a1030', color: '#a07bd4' },
    Aptos:    { bg: '#0f2a20', color: '#4ccfa0' },
};

// Parses the search box into a filter for the backend. JOB-<id> / ORD-<id>
// (case-insensitive) are exact matches; anything else is a job name search.
function parseSearchInput(raw) {
    const trimmed = raw.trim();
    if (!trimmed) return { filter: {}, error: '' };
    const match = trimmed.match(SEARCH_PATTERN);
    if (!match) {
        return { filter: { job_name: trimmed }, error: '' };
    }
    const [, prefix, digits] = match;
    return prefix.toUpperCase() === 'JOB'
        ? { filter: { job_id: digits }, error: '' }
        : { filter: { order_id: digits }, error: '' };
}

const getAdminHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('prismax_admin_token') || ''}` });

const Pagination = ({ page, totalPages, onChange }) => {
    if (totalPages <= 1) return null;
    return (
        <div className={styles.pagination}>
            <button className={styles.pageButton} onClick={() => onChange(page - 1)} disabled={page <= 1}>
                <ChevronLeft size={14} />
            </button>
            <span className={styles.pageInfo}>Page {page} of {totalPages}</span>
            <button className={styles.pageButton} onClick={() => onChange(page + 1)} disabled={page >= totalPages}>
                <ChevronRight size={14} />
            </button>
        </div>
    );
};

const CopyButton = ({ text }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        });
    };
    return (
        <button className={jobsTableStyles.copyButton} onClick={handleCopy} title="Copy">
            {copied
                ? <Check size={13} className={jobsTableStyles.copyIconDone} />
                : <Copy size={13} className={jobsTableStyles.copyIcon} />}
        </button>
    );
};

const UserCell = ({ userId, email, walletAddress, walletChain }) => {
    if (email) {
        return (
            <div className={jobsTableStyles.userCell}>
                <div className={jobsTableStyles.userEmail}>{email}</div>
                <div className={jobsTableStyles.userId}>ID: {userId}</div>
            </div>
        );
    }
    if (walletAddress) {
        const truncated = `${walletAddress.slice(0, 6)}...${walletAddress.slice(-6)}`;
        const chainStyle = CHAIN_COLORS[walletChain] || { bg: '#2a2a2a', color: '#aaaaaa' };
        return (
            <div className={jobsTableStyles.userCell}>
                <div className={jobsTableStyles.walletAddressRow}>
                    <span className={jobsTableStyles.walletAddress} title={walletAddress}>{truncated}</span>
                    <CopyButton text={walletAddress} />
                </div>
                {walletChain && (
                    <span className={jobsTableStyles.chainBadge} style={{ background: chainStyle.bg, color: chainStyle.color }}>
                        {walletChain}
                    </span>
                )}
                <div className={jobsTableStyles.userId}>ID: {userId}</div>
            </div>
        );
    }
    return (
        <div className={jobsTableStyles.userCell}>
            <div className={jobsTableStyles.userId}>ID: {userId}</div>
        </div>
    );
};

const JobsTable = () => {
    const { handleAdminSessionExpired } = useContext(SwapMainAppContext);

    const [jobs, setJobs] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const [orders, setOrders] = useState([]);

    const [searchInput, setSearchInput] = useState('');
    const [searchError, setSearchError] = useState('');
    const [appliedFilter, setAppliedFilter] = useState({});

    const [selectedJob, setSelectedJob] = useState(null);
    const [formJob, setFormJob] = useState(undefined); // undefined = closed, object = edit (no create here)
    const [deletingJob, setDeletingJob] = useState(null);
    const [unmarkingJob, setUnmarkingJob] = useState(null);

    const jobsBaseUrl = `${PRISMAX_DATA_PIPELINE_URL}/data/admin/jobs`;
    const allJobsUrl = `${jobsBaseUrl}/all`;

    const fetchJobs = useCallback(async (targetPage, filter) => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams({ page: String(targetPage), page_size: String(PAGE_SIZE) });
            if (filter.job_id) params.set('job_id', filter.job_id);
            if (filter.order_id) params.set('order_id', filter.order_id);
            if (filter.job_name) params.set('job_name', filter.job_name);

            const res = await fetch(`${allJobsUrl}?${params.toString()}`, { headers: getAdminHeaders() });
            const json = await res.json();
            if (!json.success) {
                const message = getApiErrorMessage(json, 'Failed to load jobs.');
                if (isSessionExpiredError(message)) {
                    handleAdminSessionExpired?.();
                    return;
                }
                setError(message);
                setJobs([]);
                return;
            }
            setJobs(json.data || []);
            setPage(json.pagination.page);
            setTotalPages(json.pagination.total_pages);
            setTotalCount(json.pagination.total_count);
        } catch (err) {
            if (isSessionExpiredError(err)) {
                handleAdminSessionExpired?.();
                return;
            }
            setError('Failed to load jobs.');
            setJobs([]);
        } finally {
            setLoading(false);
        }
    }, [allJobsUrl, handleAdminSessionExpired]);

    const fetchOrders = useCallback(async () => {
        try {
            const res = await fetch(`${jobsBaseUrl}/available-orders`, { headers: getAdminHeaders() });
            if (!res.ok) throw new Error('Failed to load orders');
            const json = await res.json();
            setOrders(json.data || []);
        } catch (err) {
            console.error(err);
        }
    }, [jobsBaseUrl]);

    useEffect(() => { fetchJobs(1, {}); }, [fetchJobs]);
    useEffect(() => { fetchOrders(); }, [fetchOrders]);

    const handleSearchSubmit = () => {
        const { filter, error: parseError } = parseSearchInput(searchInput);
        if (parseError) {
            setSearchError(parseError);
            return;
        }
        setSearchError('');
        setAppliedFilter(filter);
        fetchJobs(1, filter);
    };

    const handleSearchClear = () => {
        setSearchInput('');
        setSearchError('');
        setAppliedFilter({});
        fetchJobs(1, {});
    };

    const hasActiveFilter = !!(appliedFilter.job_id || appliedFilter.order_id || appliedFilter.job_name);

    const handleRowClick = (job) => {
        setSelectedJob((prev) => (prev?.job_id === job.job_id ? null : job));
    };

    const handleMarkPaid = async (e, job) => {
        e.stopPropagation();
        try {
            await markPaid(job);
        } catch (err) {
            console.error(err);
        }
    };

    const markPaid = useCallback(async (job) => {
        const res = await fetch(`${jobsBaseUrl}/${job.job_id}/payments`, { method: 'POST', headers: getAdminHeaders() });
        const json = await res.json();
        if (!res.ok || !json.success) throw new Error(json.msg || 'Failed to record payment');
        setJobs((prev) => prev.map((j) => (j.job_id === job.job_id ? { ...j, ...json.data } : j)));
        setSelectedJob((prev) => (prev && prev.job_id === job.job_id ? { ...prev, ...json.data } : prev));
        setFormJob((prev) => (prev && prev.job_id === job.job_id ? { ...prev, ...json.data } : prev));
        return json.data;
    }, [jobsBaseUrl]);

    // Opens the unmark confirmation modal (from the table row action or from
    // the edit modal's "Not paid" segment — in the latter case, close the
    // edit modal first rather than stacking two overlays).
    const handleRequestUnmark = (job) => {
        setFormJob(undefined);
        setUnmarkingJob(job);
    };

    const voidPayments = useCallback((updatedJob) => {
        setJobs((prev) => prev.map((j) => (j.job_id === updatedJob.job_id ? { ...j, ...updatedJob } : j)));
        setSelectedJob((prev) => (prev && prev.job_id === updatedJob.job_id ? { ...prev, ...updatedJob } : prev));
        setFormJob((prev) => (prev && prev.job_id === updatedJob.job_id ? { ...prev, ...updatedJob } : prev));
        setUnmarkingJob(null);
    }, []);

    const handleJobSaved = () => {
        setFormJob(undefined);
        fetchJobs(page, appliedFilter);
    };

    const handleJobDeleted = (jobId) => {
        setDeletingJob(null);
        if (selectedJob?.job_id === jobId) setSelectedJob(null);
        // Deleting shifts how many jobs remain on this page — if this was the
        // last row on a page beyond the first, step back a page; otherwise
        // just refresh the current page.
        const nextPage = jobs.length <= 1 && page > 1 ? page - 1 : page;
        fetchJobs(nextPage, appliedFilter);
    };

    return (
        <div className={styles.section}>
            <h1 className={styles.title}>Jobs</h1>
            <hr className={styles.divider} />

            <div className={jobsTableStyles.searchRow}>
                <input
                    className={jobsTableStyles.searchInput}
                    type="text"
                    placeholder="Search job name, JOB-123 or ORD-45"
                    value={searchInput}
                    onChange={(e) => { setSearchInput(e.target.value); setSearchError(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleSearchSubmit(); }}
                />
                <button className={jobsTableStyles.searchButton} onClick={handleSearchSubmit}>Search</button>
                {hasActiveFilter && (
                    <button className={jobsTableStyles.searchClear} onClick={handleSearchClear}>Clear</button>
                )}
                <span className={jobsTableStyles.resultCount}>{totalCount} job{totalCount === 1 ? '' : 's'}</span>
            </div>
            {searchError && <p className={styles.errorMsg}>{searchError}</p>}

            {loading && <p className={styles.stateMsg}>Loading…</p>}
            {error && <p className={styles.errorMsg}>{error}</p>}
            {!loading && !error && jobs.length === 0 && (
                <p className={styles.empty}>No jobs found.</p>
            )}

            {!loading && !error && jobs.length > 0 && (
                <>
                    <div className={styles.tableWrapper}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th className={styles.th}>Job</th>
                                    <th className={styles.th}>User</th>
                                    <th className={styles.th}>Task names</th>
                                    <th className={styles.th}>Rate</th>
                                    <th className={styles.th}>Hours logged</th>
                                    <th className={styles.th}>Amount</th>
                                    <th className={styles.th}>Assigned date</th>
                                    <th className={styles.th}>Due date</th>
                                    <th className={styles.th}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {jobs.map((job) => {
                                    const isSelected = selectedJob?.job_id === job.job_id;
                                    const pay = paymentState(job);
                                    return (
                                        <tr
                                            key={job.job_id}
                                            className={`${styles.row} ${styles.rowClickable} ${isSelected ? jobsTableStyles.rowSelected : ''}`}
                                            onClick={() => handleRowClick(job)}
                                        >
                                            <td className={styles.cell}>
                                                <JobCell job={job} showOrder />
                                            </td>
                                            <td className={styles.cell}>
                                                <UserCell
                                                    userId={job.assigned_user_id}
                                                    email={job.user_email}
                                                    walletAddress={job.user_wallet_address}
                                                    walletChain={job.user_wallet_chain}
                                                />
                                            </td>
                                            <td className={styles.cell}><TaskChips tasks={job.tasks} /></td>
                                            <td className={styles.cell}>
                                                <span className={cellStyles.rateVal}>{fmtUSD(job.rate_usd_per_hour)}</span>
                                                <span className={cellStyles.rateUnit}>/h</span>
                                            </td>
                                            <td className={styles.cell}><ProgressCell job={job} /></td>
                                            <td className={styles.cell}><AmountCell job={job} /></td>
                                            <td className={`${styles.cell} ${cellStyles.dateCell}`}>{job.created_at ? job.created_at.slice(0, 10) : '—'}</td>
                                            <td className={styles.cell}><DueCell dueDate={job.due_date} /></td>
                                            <td className={`${styles.cell} ${cellStyles.actionCell}`} onClick={(e) => e.stopPropagation()}>
                                                <div className={cellStyles.rowActions}>
                                                    <button className={cellStyles.actEdit} onClick={(e) => { e.stopPropagation(); setFormJob(job); }}>Edit</button>
                                                    {pay.key !== 'none' && pay.key !== 'paid' && (
                                                        <button className={cellStyles.actPay} onClick={(e) => handleMarkPaid(e, job)}>Mark paid</button>
                                                    )}
                                                    {pay.key === 'paid' && (
                                                        <button
                                                            className={cellStyles.actPay}
                                                            onClick={(e) => { e.stopPropagation(); setUnmarkingJob(job); }}
                                                        >Unmark</button>
                                                    )}
                                                    <button className={cellStyles.actDelete} onClick={(e) => { e.stopPropagation(); setDeletingJob(job); }}>Delete</button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <Pagination page={page} totalPages={totalPages} onChange={(p) => fetchJobs(p, appliedFilter)} />
                </>
            )}

            {selectedJob && (
                <JobDetailDrawer
                    job={selectedJob}
                    onClose={() => setSelectedJob(null)}
                    getHeaders={getAdminHeaders}
                    jobsBaseUrl={jobsBaseUrl}
                />
            )}

            {formJob !== undefined && (
                <JobFormModal
                    job={formJob}
                    orders={orders}
                    adminUserId={formJob?.assigned_user_id}
                    onCancel={() => setFormJob(undefined)}
                    onSaved={handleJobSaved}
                    onMarkPaid={markPaid}
                    onRequestUnmark={handleRequestUnmark}
                    getHeaders={getAdminHeaders}
                    jobsBaseUrl={jobsBaseUrl}
                />
            )}

            {deletingJob && (
                <JobDeleteModal
                    job={deletingJob}
                    onCancel={() => setDeletingJob(null)}
                    onDeleted={handleJobDeleted}
                    getHeaders={getAdminHeaders}
                    jobsBaseUrl={jobsBaseUrl}
                />
            )}

            {unmarkingJob && (
                <UnmarkPaymentsModal
                    job={unmarkingJob}
                    onCancel={() => setUnmarkingJob(null)}
                    onVoided={voidPayments}
                    getHeaders={getAdminHeaders}
                    jobsBaseUrl={jobsBaseUrl}
                />
            )}
        </div>
    );
};

export default JobsTable;
